import React from 'react';
import useLang from '../../i18n/useLang';
import Button from './Button';
import { Reveal } from './motion';
import { WhatsAppIcon, whatsappHref } from './contact';

// Inner-page header: big headline on a soft tinted field.
export function PageHero({ title, lead, tone = 'mint', children }) {
  const tones = {
    mint: 'bg-mint-50',
    sky: 'bg-sky-50',
    fog: 'bg-fog-50',
  };
  return (
    <section className={`relative overflow-hidden ${tones[tone]}`}>
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 end-[-10rem] h-[28rem] w-[28rem] rounded-full bg-white/70 blur-3xl" />
      <div className="wrap relative pb-16 pt-16 sm:pb-20 sm:pt-24">
        <h1 className="display max-w-[18ch] text-[2.75rem] text-ink sm:text-6xl lg:text-[4.5rem]">{title}</h1>
        {lead && <p className="lead mt-6 max-w-[58ch]">{lead}</p>}
        {children}
      </div>
    </section>
  );
}

// Closing call to action shared by every page.
export function CtaBanner() {
  const { t } = useLang();
  return (
    <section className="wrap py-20 sm:py-28">
      <Reveal variant="scale" className="relative isolate overflow-hidden rounded-[2.5rem] bg-navy-900 px-6 py-16 text-white sm:px-14 sm:py-20">
        <div aria-hidden="true" className="drift absolute -bottom-40 -end-24 -z-10 h-[30rem] w-[30rem] rounded-full bg-grovv/25 blur-3xl" />
        <div aria-hidden="true" className="drift-slow absolute -top-48 start-1/4 -z-10 h-[24rem] w-[24rem] rounded-full bg-sky-200/10 blur-3xl" />
        <div className="grid grid-cols-1 items-end gap-10 lg:grid-cols-[1.5fr_1fr]">
          <div>
            <h2 className="heading max-w-[20ch] text-4xl sm:text-5xl lg:text-[3.4rem]">{t('home.cta.title')}</h2>
            <p className="mt-5 max-w-[50ch] text-lg leading-relaxed text-navy-200">{t('home.cta.body')}</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Button to="/contact" size="lg" arrow>
              {t('site.cta')}
            </Button>
            <Button href={whatsappHref(t('site.whatsapp.message'))} target="_blank" rel="noopener noreferrer" size="lg" variant="onDark">
              <WhatsAppIcon className="h-5 w-5" />
              {t('site.whatsapp.label')}
            </Button>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export function SectionHeading({ title, lead, className = '', align = 'start' }) {
  return (
    <div className={`${align === 'center' ? 'mx-auto text-center' : ''} max-w-3xl ${className}`}>
      <h2 className="heading text-[2.25rem] text-ink sm:text-5xl">{title}</h2>
      {lead && <p className={`lead mt-5 ${align === 'center' ? 'mx-auto' : ''} max-w-[56ch]`}>{lead}</p>}
    </div>
  );
}

// Tool and protocol names as mono chips. The row stays LTR; in Hebrew it aligns to the right.
export function Chips({ items, className = 'mt-6', onTint = false }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <ul dir="ltr" className={`${className} flex flex-wrap gap-2 rtl:justify-end`}>
      {items.map((s) => (
        <li
          key={s}
          className={`rounded-full px-2.5 py-1 font-mono text-[11.5px] text-ink-600 ${onTint ? 'bg-white/80 ring-1 ring-inset ring-fog-200' : 'bg-fog-100'}`}
        >
          {s}
        </li>
      ))}
    </ul>
  );
}
